import { useMemo } from 'react';
import { HEATMAP_RAMP, HEATMAP_RAMP_OPACITY, rgbTriple } from '@repo/design-tokens';
import type { HeatGridCells } from '@repo/front-end-utils';
import { countHeatGridPaint } from './heatGridTiming';

/**
 * The heat history as ONE image (`observer-autogen-heat-history-view`, R4–R6; card 25,
 * `packages/ui/design-cards/components-25-heat-history.html`).
 *
 * The grid is painted at CELL resolution — one pixel per 16 px cell of the picture — and the plotter
 * scales that small image up by 16 inside its heat layer, letting the browser smooth it. So a 14-day
 * grid is a few thousand pixels, painted once per (cells, picture size), and a zoom or pan moves the
 * image without repainting it: there is no element per keypoint or per cell.
 */

/** The grid's cell size in picture pixels (the read API's default and the stored grain). */
export const HEAT_GRID_CELL_PX = 16;

export interface HeatGridRaster {
  /** Straight (non-premultiplied) RGBA, row-major, `width × height` pixels. */
  rgba: Uint8ClampedArray;
  width: number;
  height: number;
  /** The largest count drawn. */
  peak: number;
  /** The 99th-percentile non-zero count drawn: the count that reaches the top stop. */
  p99: number;
}

const RAMP_RGB: ReadonlyArray<readonly [number, number, number]> = HEATMAP_RAMP.map(hex => {
  const [r, g, b] = rgbTriple(hex).split(',').map(Number);
  return [r, g, b] as const;
});
const STOPS = HEATMAP_RAMP.length;

/**
 * Which ramp stop a count paints at: `clamp(ceil(STOPS × √(n / p99)), 1, STOPS)`. The square root
 * and the 99th-percentile ceiling are the design's ruling (Mike, 2026-10-12): one very hot cell
 * does not wash out the rest. Returns 0 for a zero count, which is not painted.
 */
export const heatStopFor = (n: number, p99: number): number => {
  if (n <= 0 || p99 <= 0) return 0;
  return Math.min(STOPS, Math.max(1, Math.ceil(STOPS * Math.sqrt(n / p99))));
};

const percentile99 = (values: number[]): number => {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  // Linear interpolation between the closest ranks, as Postgres's percentile_cont does, so the
  // browser's ceiling and the one the design was drawn with agree on the same counts.
  const rank = 0.99 * (sorted.length - 1);
  const lo = Math.floor(rank);
  const hi = Math.ceil(rank);
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (rank - lo);
};

/**
 * Pure: decoded cells and the picture's natural size → the cell-resolution image's pixels.
 * Cells outside `ceil(width/16) × ceil(height/16)` are dropped (spec R5): the grid is drawn in the
 * picture's own pixels, unscaled, as today's dots are.
 */
export const rasteriseHeatGrid = (
  cells: HeatGridCells,
  naturalSize: { w: number; h: number },
): HeatGridRaster => {
  const width = Math.max(0, Math.ceil(naturalSize.w / HEAT_GRID_CELL_PX));
  const height = Math.max(0, Math.ceil(naturalSize.h / HEAT_GRID_CELL_PX));
  const rgba = new Uint8ClampedArray(width * height * 4);
  const inside: number[] = [];
  const counts: number[] = [];
  for (let i = 0; i < cells.n.length; i += 1) {
    const x = cells.cx[i];
    const y = cells.cy[i];
    const n = cells.n[i];
    if (n > 0 && x >= 0 && y >= 0 && x < width && y < height) {
      inside.push(i);
      counts.push(n);
    }
  }
  const p99 = percentile99(counts);
  let peak = 0;
  for (const i of inside) {
    const n = cells.n[i];
    if (n > peak) peak = n;
    const stop = heatStopFor(n, p99);
    const [r, g, b] = RAMP_RGB[stop - 1];
    const at = (cells.cy[i] * width + cells.cx[i]) * 4;
    rgba[at] = r;
    rgba[at + 1] = g;
    rgba[at + 2] = b;
    rgba[at + 3] = Math.round(HEATMAP_RAMP_OPACITY[stop - 1] * 255);
  }
  return { rgba, width, height, peak, p99 };
};

/**
 * Browser only: the raster as a PNG data URL, or null where no 2D canvas exists (jsdom). A data URL
 * rather than an object URL: the image is a few kilobytes, and nothing has to be revoked when it is
 * replaced or unmounted.
 */
export const heatGridToDataUrl = (raster: HeatGridRaster): string | null => {
  if (raster.width === 0 || raster.height === 0 || typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = raster.width;
  canvas.height = raster.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    // A lost or refused context draws nothing; say so rather than leave the picture quietly bare.
    console.warn(
      '[observer-editor] no 2D canvas for the heat history image',
      raster.width,
      raster.height,
    );
    return null;
  }
  ctx.putImageData(new ImageData(raster.rgba, raster.width, raster.height), 0, 0);
  return canvas.toDataURL('image/png');
};

/** What the plotter draws: the cell-resolution image and its size in cells. */
export interface HeatGridImage {
  url: string;
  width: number;
  height: number;
}

/** Rasterise and encode: the whole paint, as one call the hook makes once per (cells, size). */
export const paintHeatGrid = (
  cells: HeatGridCells,
  naturalSize: { w: number; h: number },
): HeatGridImage | null => {
  const raster = rasteriseHeatGrid(cells, naturalSize);
  const url = heatGridToDataUrl(raster);
  return url ? { url, width: raster.width, height: raster.height } : null;
};

/**
 * Paints the grid once per (cells, picture size). Keyed on those and nothing else — never on the
 * view — so a zoom or a pan cannot repaint it, even when the host re-renders on every zoom step
 * (spec R6, criterion 4). `paint` is injectable so a test can count paints.
 */
export const useHeatGridImage = (
  cells: HeatGridCells | null,
  naturalSize: { w: number; h: number } | null,
  paint: typeof paintHeatGrid = paintHeatGrid,
): HeatGridImage | null => {
  const w = naturalSize?.w ?? 0;
  const h = naturalSize?.h ?? 0;
  return useMemo(() => {
    if (!cells || w <= 0 || h <= 0) return null;
    countHeatGridPaint();
    return paint(cells, { w, h });
  }, [cells, w, h, paint]);
};
